<template>
  <div class="container mt-5">
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-6">
        <div class="card">
          <div class="card-body p-4 p-md-5">
            <div class="text-center mb-4">
              <h3 class="card-title mb-3">Crear Cuenta</h3>
              <p class="text-center mt-5 ">
                <strong>Prueba gratis por 14 días:</strong> accede a todas las funciones de nuestro software médico sin
                compromiso y descubre cómo optimizar la gestión de tu consultorio.
              </p>
            </div>
            <form @submit.prevent="handleSignup">

              <!-- General Error Message -->
              <div v-if="errorMessage" class="alert alert-danger" role="alert">
                {{ errorMessage }}
              </div>

              <!-- Success Redirect Message -->
              <div v-if="successRedirectMessage" class="alert alert-success" role="alert">
                {{ successRedirectMessage }}
              </div>

              <div class="mb-3">
                <label for="name" class="form-label">Nombre Completo</label>
                <input type="text" class="form-control" id="name" v-model="name" required placeholder="Tu Nombre"
                  :disabled="isLoading" aria-describedby="nameHelp">
                <div id="nameHelp" class="form-text">Ejemplo: Dra. Denise Medina</div>
              </div>
              <div class="mb-3">
                <label for="email" class="form-label">Correo Electrónico</label>
                <input type="email" class="form-control" :class="{'is-invalid': emailError}" id="email" v-model="email"
                  required placeholder="tu@email.com" aria-describedby="emailHelp" :disabled="isLoading">
                <div id="emailHelp" class="invalid-feedback">
                  {{ emailError }}
                </div>
              </div>
              <div class="mb-3">
                <label for="password" class="form-label">Contraseña</label>
                <input type="password" class="form-control" :class="{'is-invalid': passwordError}" id="password"
                  v-model="password" required placeholder="Mínimo 6 caracteres" aria-describedby="passwordHelp"
                  :disabled="isLoading">
                <div id="passwordHelp" class="invalid-feedback">
                  {{ passwordError }}
                </div>
              </div>
              <div class="mb-3">
                <label for="confirmPassword" class="form-label">Confirmar Contraseña</label>
                <input type="password" class="form-control" :class="{'is-invalid': confirmPasswordError}"
                  id="confirmPassword" v-model="confirmPassword" required placeholder="Repite la contraseña"
                  aria-describedby="confirmPasswordHelp" :disabled="isLoading">
                <div id="confirmPasswordHelp" class="invalid-feedback">
                  {{ confirmPasswordError }}
                </div>
              </div>
              <div class="form-check mb-3">
                <input class="form-check-input" :class="{'is-invalid': termsError}" type="checkbox" value="" id="terms"
                  v-model="termsAccepted" required :disabled="isLoading" aria-describedby="termsHelp">
                <label class="form-check-label" for="terms">
                  Acepto los <router-link to="/terms-conditions" class="text-primary small" tabindex="-1">Términos y
                    condiciones</router-link>
                </label>
                <div id="termsHelp" class="invalid-feedback">
                  {{ termsError }}
                </div>
              </div>
              <div class="d-grid mb-3">
                <button type="submit" class="btn btn-primary" :disabled="isLoading">
                  <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status"
                    aria-hidden="true"></span>
                  {{ isLoading ? ' Registrando...' : 'Registrarse' }}
                </button>
              </div>
              <div class="text-center">
                <small class="text-muted">¿Ya tienes cuenta? <a href="https://app.medicynia.com" target="_blank"
                    rel="noopener">Inicia sesión aquí</a></small>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { createUserWithEmailAndPassword } from 'firebase/auth'; // Import specific functions
  import { auth } from '@/fb'; // Import the auth instance from your fb.js
  import { useHead } from '@vueuse/head'

  export default {
    name: 'SignupView',
    setup() {
      useHead({
        title: 'Crear Cuenta | Medicynia',
        meta: [
          { name: 'description', content: 'Regístrate en Medicynia y comienza a gestionar tu consultorio médico de forma digital, segura y conforme a la NOM-004.' },
          { property: 'og:title', content: 'Crear Cuenta | Medicynia' },
          { property: 'og:description', content: 'Regístrate en Medicynia y comienza a gestionar tu consultorio médico de forma digital, segura y conforme a la NOM-004.' },
          { property: 'og:type', content: 'website' },
          { property: 'og:url', content: 'https://medicynia.com/signup' },
          { property: 'og:image', content: 'https://medicynia.com/logo-main.webp' },
          { name: 'robots', content: 'index, follow' }
        ],
        link: [
          { rel: 'canonical', href: 'https://medicynia.com/signup' }
        ]
      })
    },
    data() {
      return {
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        termsAccepted: false,
        isLoading: false, // Added for loading state
        errorMessage: '', // Added for general errors
        emailError: '', // Specific error for email
        passwordError: '', // Specific error for password
        confirmPasswordError: '', // Specific error for confirm password
        termsError: '', // Specific error for terms
        successRedirectMessage: '' // Message for successful registration and redirect
      };
    },
    methods: {
      async handleSignup() {
        // Reset errors
        this.errorMessage = '';
        this.emailError = '';
        this.passwordError = '';
        this.confirmPasswordError = '';
        this.termsError = '';
        let isValid = true;

        // Basic Validations
        if (this.password !== this.confirmPassword) {
          this.confirmPasswordError = 'Las contraseñas no coinciden.';
          isValid = false;
        }
        // Firebase requires password >= 6 chars
        if (this.password.length < 6) {
          this.passwordError = 'La contraseña debe tener al menos 6 caracteres.';
          isValid = false;
        }
        if (!this.termsAccepted) {
          this.termsError = 'Debes aceptar los términos y condiciones.';
          isValid = false;
        }

        if (!isValid) {
          return; // Stop if basic validations fail
        }


        this.isLoading = true; // Start loading

        try {
          // 1. Create user in Firebase Auth
          const userCredential = await createUserWithEmailAndPassword(auth, this.email, this.password);
          const user = userCredential.user;
          console.log('Firebase Signup successful:', user);

          // 2. Register user in your backend
          if (user && user.uid) {
            try {
              const backendResponse = await fetch('https://expedienteback.com/registrar-personal-medico', { // Assuming HTTPS
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ uid: user.uid }),
              });

              const backendData = await backendResponse.json();


              if (backendResponse.ok && backendData.status === 'success') {
                // Backend registration successful
                console.log('Backend registration successful:', backendData);
                // 3. Show redirect message and redirect after a short delay
                this.successRedirectMessage = '¡Registro exitoso! En unos segundos serás redireccionado al sistema para tu primer inicio de sesión...';
                // Google Ads Conversion Tracking
                if (typeof window.gtag === 'function') {
                  window.gtag('event', 'conversion', {'send_to': 'AW-699025502/87AZCJScwMsaEN6Qqc0C'});
                }
                setTimeout(() => {
                  window.location.href = 'https://app.medicynia.com/';
                }, 3500); // 3.5 segundos
                return; // Exit function after showing message and scheduling redirect

              } else {
                // Backend registration failed - Show error, stop loading, DO NOT redirect
                console.error('Backend registration failed:', backendData.message || `HTTP status ${backendResponse.status}`);
                // Use the specific message from backend if available
                this.errorMessage = `Error al registrar en el sistema médico: ${backendData.message || 'Error desconocido del servidor.'}`;
                this.isLoading = false;
                return; // Stop execution
              }
            } catch (backendError) {
              // Network or other errors calling the backend - Show error, stop loading, DO NOT redirect
              console.error('Error calling backend:', backendError);
              this.errorMessage = 'No se pudo conectar con el sistema médico para completar el registro. Por favor, inténtalo más tarde.';
              this.isLoading = false;
              return; // Stop execution
            }
          } else {
            // Should not happen if Firebase signup was successful, but handle defensively
            console.error('Firebase user or UID not found after signup.');
            this.errorMessage = 'Ocurrió un error inesperado después de crear la cuenta. No se encontró el identificador de usuario.';
            this.isLoading = false;
            return; // Stop execution
          }

        } catch (error) {
          // Handle Firebase Auth errors - Show error, stop loading, DO NOT redirect
          console.error('Firebase Signup error:', error.code, error.message);
          this.isLoading = false; // Stop loading on Firebase error

          switch (error.code) {
            case 'auth/email-already-in-use':
              this.emailError = 'Este correo electrónico ya está registrado.';
              break;
            case 'auth/invalid-email':
              this.emailError = 'El formato del correo electrónico no es válido.';
              break;
            case 'auth/weak-password':
              this.passwordError = 'La contraseña es demasiado débil. Debe tener al menos 6 caracteres.';
              break;
            default:
              // Use a more generic error message for Firebase issues
              this.errorMessage = 'Ocurrió un error durante el registro. Por favor, verifica tus datos e inténtalo de nuevo.';
          }
        }
      }
    }
  }
</script>

<style scoped>
  .card {
    border: none;
  }

  .form-check-label a {
    text-decoration: none;
  }

  .form-check-label a:hover {
    text-decoration: underline;
  }

  /* Add styles for invalid feedback to ensure visibility */
  .invalid-feedback {
    display: block;
    /* Make sure feedback is shown */
    width: 100%;
    margin-top: .25rem;
    font-size: .875em;
    /* Bootstrap's default invalid-feedback font-size */
    color: var(--bs-danger);
    /* Bootstrap's default danger color */
  }

  .form-control.is-invalid {
    border-color: var(--bs-danger);
    /* Ensure border color changes */
  }

  .form-check-input.is-invalid~.form-check-label {
    color: var(--bs-danger);
    /* Optional: Change label color */
  }

  .form-check-input.is-invalid {
    border-color: var(--bs-danger);
  }
</style>